'use client';

import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { User, apiService } from '@/lib/api';

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (phone: string, password: string, deviceName?: string) => Promise<boolean>;
  register: (name: string, phone: string, password: string) => Promise<boolean>;
  verifyOtp: (phone: string, otp: string) => Promise<boolean>;
  logout: () => Promise<void>;
  forgotPassword: (phone: string) => Promise<boolean>;
  resetPassword: (phone: string, otp: string, newPassword: string, confirmPassword: string) => Promise<boolean>;
  changePassword: (currentPassword: string, newPassword: string, confirmPassword: string) => Promise<boolean>;
  refreshUser: () => Promise<void>;
  refreshBalance: () => Promise<void>; // Dedicated function to refresh user balance
  clearAuthData: () => void; // Utility function to clear all auth data
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const isAuthenticated = !!user && apiService.isAuthenticated();

  // Define refreshUser function with useCallback to avoid dependency issues
  const refreshUser = useCallback(async (): Promise<void> => {
    try {
      const response = await apiService.getUserProfile();
      if (response.success) {
        setUser(response.data.profile);
        // Update user data in localStorage
        if (typeof window !== 'undefined') {
          localStorage.setItem('user_data', JSON.stringify(response.data.profile));
        }
      }
    } catch (error) {
      console.error('Failed to refresh user:', error);
      setUser(null);
      apiService.clearToken();
      // Clear user data from localStorage on error
      if (typeof window !== 'undefined') {
        localStorage.removeItem('user_data');
      }
    }
  }, []);

  // Define refreshBalance function to specifically refresh user balance
  const refreshBalance = useCallback(async (): Promise<void> => {
    try {
      const response = await apiService.getUserProfile();
      if (response.success && response.data.profile) {
        // Update only the balance fields in the current user state
        setUser(prevUser => {
          if (prevUser) {
            return {
              ...prevUser,
              balance: response.data.profile.balance,
              p_balance: response.data.profile.p_balance
            };
          }
          return response.data.profile;
        });
        
        // Update user data in localStorage
        if (typeof window !== 'undefined') {
          const currentUserData = localStorage.getItem('user_data');
          if (currentUserData) {
            const userData = JSON.parse(currentUserData);
            const updatedUserData = {
              ...userData,
              balance: response.data.profile.balance,
              p_balance: response.data.profile.p_balance
            };
            localStorage.setItem('user_data', JSON.stringify(updatedUserData));
          }
        }
      }
    } catch (error) {
      console.error('Failed to refresh balance:', error);
    }
  }, []);

  // Load user data from localStorage on initialization
  // This ensures users stay logged in after page refresh
  useEffect(() => {
    const loadUserFromStorage = () => {
      if (typeof window !== 'undefined') {
        const storedUser = localStorage.getItem('user_data');
        if (storedUser) {
          try {
            const userData = JSON.parse(storedUser);
            setUser(userData);
          } catch (error) {
            console.error('Failed to parse stored user data:', error);
            localStorage.removeItem('user_data');
          }
        }
      }
    };

    // Use setTimeout to ensure localStorage is loaded before authentication check
    const timer = setTimeout(loadUserFromStorage, 0);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        if (apiService.isAuthenticated() && !apiService.isTokenExpired()) {
          // Try to refresh user data from server
          await refreshUser();
        } else {
          // Clear user data if no valid token or token is expired
          setUser(null);
          apiService.clearToken();
          if (typeof window !== 'undefined') {
            localStorage.removeItem('user_data');
          }
        }
      } catch (error) {
        console.error('Failed to initialize auth:', error);
        setUser(null);
        apiService.clearToken();
        if (typeof window !== 'undefined') {
          localStorage.removeItem('user_data');
        }
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, [refreshUser]);

  const login = async (phone: string, password: string, deviceName?: string): Promise<boolean> => {
    try {
      setIsLoading(true);
      const response = await apiService.login({
        phone,
        password,
        device_name: deviceName || 'Web Browser',
      });

      if (response.success) {
        setUser(response.data.user);
        // Save user data to localStorage
        if (typeof window !== 'undefined') {
          localStorage.setItem('user_data', JSON.stringify(response.data.user));
        }
        return true;
      }
      return false;
    } catch (error) {
      console.error('Login failed:', error);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const register = async (name: string, phone: string, password: string): Promise<boolean> => {
    try {
      setIsLoading(true);
      const response = await apiService.register({ name, phone, password });
      return response.success;
    } catch (error) {
      console.error('Registration failed:', error);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const verifyOtp = async (phone: string, otp: string): Promise<boolean> => {
    try {
      setIsLoading(true);
      const response = await apiService.verifyOtp({ phone, otp });
      
      if (response.success) {
        setUser(response.data.user);
        // Save user data to localStorage
        if (typeof window !== 'undefined') {
          localStorage.setItem('user_data', JSON.stringify(response.data.user));
        }
        return true;
      }
      return false;
    } catch (error) {
      console.error('OTP verification failed:', error);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async (): Promise<void> => {
    try {
      await apiService.logout();
    } catch (error) {
      console.error('Logout failed:', error);
    } finally {
      setUser(null);
      apiService.clearToken();
      // Clear user data from localStorage
      if (typeof window !== 'undefined') {
        localStorage.removeItem('user_data');
      }
    }
  };

  const forgotPassword = async (phone: string): Promise<boolean> => {
    try {
      setIsLoading(true);
      const response = await apiService.forgotPassword({ phone });
      return response.success;
    } catch (error) {
      console.error('Forgot password failed:', error);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const resetPassword = async (
    phone: string,
    otp: string,
    newPassword: string,
    confirmPassword: string
  ): Promise<boolean> => {
    try {
      setIsLoading(true);
      const response = await apiService.resetPassword({
        phone,
        otp,
        new_password: newPassword,
        confirm_password: confirmPassword,
      });
      return response.success;
    } catch (error) {
      console.error('Reset password failed:', error);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const changePassword = async (
    currentPassword: string,
    newPassword: string,
    confirmPassword: string
  ): Promise<boolean> => {
    try {
      setIsLoading(true);
      const response = await apiService.changePassword({
        current_password: currentPassword,
        new_password: newPassword,
        confirm_password: confirmPassword,
      });
      
      if (response.success) {
        setUser(null);
        apiService.clearToken();
        // Clear user data from localStorage
        if (typeof window !== 'undefined') {
          localStorage.removeItem('user_data');
        }
      }
      
      return response.success;
    } catch (error) {
      console.error('Change password failed:', error);
      return false;
    } finally {
      setIsLoading(false);
    }
  };


  const clearAuthData = (): void => {
    setUser(null);
    apiService.clearToken();
    if (typeof window !== 'undefined') {
      localStorage.removeItem('user_data');
    }
  };

  const value: AuthContextType = {
    user,
    isAuthenticated,
    isLoading,
    login,
    register,
    verifyOtp,
    logout,
    forgotPassword,
    resetPassword,
    changePassword,
    refreshUser,
    refreshBalance,
    clearAuthData,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
